"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Send, ChefHat, Clock } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Textarea } from "./ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select"
import { useCreatePrivateChefRequestMutation } from "../store"
import { toast } from "sonner"

interface PrivateChefRequestFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PrivateChefRequestForm({ open, onOpenChange }: PrivateChefRequestFormProps) {
  const [createPrivateChefRequest, { isLoading }] = useCreatePrivateChefRequestMutation()
  
  const [formData, setFormData] = useState({
    // Client Information
    fullName: "",
    phoneNumber: "",
    email: "",
    preferredContactMethod: "",
    
    // Service Details
    serviceType: "",
    serviceDates: "",
    serviceTime: "",
    location: "",
    numberOfGuests: "",
    
    // Menu Preferences
    cuisinePreference: "",
    specificDishes: "",
    dietaryRestrictions: "",
    spiceLevel: "",
    
    // Meal Structure
    numberOfCourses: "",
    servingStyle: "",
    ingredientsProvided: "",
    
    // Budget and Expectation
    budgetRange: "",
    
    // Additional Notes
    additionalNotes: "",
  })

  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSelectChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate required fields
    if (!formData.serviceType || !formData.cuisinePreference || !formData.numberOfCourses || 
        !formData.servingStyle || !formData.ingredientsProvided) {
      toast.error("Please fill in all required fields")
      return
    }

    try {
      await createPrivateChefRequest({
        fullName: formData.fullName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        preferredContactMethod: formData.preferredContactMethod,
        serviceType: formData.serviceType,
        serviceDates: formData.serviceDates,
        serviceTime: formData.serviceTime,
        location: formData.location,
        numberOfGuests: parseInt(formData.numberOfGuests, 10),
        cuisinePreference: formData.cuisinePreference,
        specificDishes: formData.specificDishes || undefined,
        dietaryRestrictions: formData.dietaryRestrictions || undefined,
        spiceLevel: formData.spiceLevel,
        numberOfCourses: formData.numberOfCourses,
        servingStyle: formData.servingStyle,
        ingredientsProvided: formData.ingredientsProvided,
        budgetRange: formData.budgetRange,
        additionalNotes: formData.additionalNotes || undefined,
      }).unwrap()

      setIsSubmitted(true)
      
      // Reset form after showing success
      setTimeout(() => {
        setIsSubmitted(false)
        setFormData({
          fullName: "",
          phoneNumber: "",
          email: "",
          preferredContactMethod: "",
          serviceType: "",
          serviceDates: "",
          serviceTime: "",
          location: "",
          numberOfGuests: "",
          cuisinePreference: "",
          specificDishes: "",
          dietaryRestrictions: "",
          spiceLevel: "",
          numberOfCourses: "",
          servingStyle: "",
          ingredientsProvided: "",
          budgetRange: "",
          additionalNotes: "",
        })
        onOpenChange(false)
      }, 3000)
    } catch (error: any) {
      toast.error(error?.data?.error || "Failed to submit request. Please try again.")
    }
  }

  if (isSubmitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="rounded-full bg-green-100 dark:bg-green-900/20 w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Send className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Request Submitted!</h3>
            <p className="text-muted-foreground mb-2">
              Thank you for your private chef service request. We've received your information and will contact you to discuss your requirements.
            </p>
            <p className="text-sm text-muted-foreground font-medium">
              We'll contact you via your preferred method: {formData.preferredContactMethod || "Email"}
            </p>
          </motion.div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-orange-500/10 p-2">
              <ChefHat className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <DialogTitle className="text-2xl">Private Chef Service Request</DialogTitle>
              <DialogDescription>
                Fill out the form below and we'll get back to you to discuss your culinary needs.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Information Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold pb-2 border-b">Client Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder="John Doe"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phoneNumber">Phone Number *</Label>
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  placeholder="0901 710 4173"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="preferredContactMethod">Preferred Method of Contact *</Label>
                <Select
                  value={formData.preferredContactMethod}
                  onValueChange={(value) => handleSelectChange("preferredContactMethod", value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contact method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Service Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold pb-2 border-b">Service Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="serviceType">Type of Service *</Label>
                <Select
                  value={formData.serviceType}
                  onValueChange={(value) => handleSelectChange("serviceType", value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private-home-dining">Private Home Dining</SelectItem>
                    <SelectItem value="weekly-meal-prep">Weekly Meal Prep</SelectItem>
                    <SelectItem value="special-occasion">Special Occasion</SelectItem>
                    <SelectItem value="intimate-event">Intimate Event</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="serviceDates">Service Date(s) *</Label>
                <Input
                  id="serviceDates"
                  name="serviceDates"
                  type="text"
                  placeholder="e.g., March 15, 2024 or Multiple dates"
                  value={formData.serviceDates}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="serviceTime">Service Time *</Label>
                <Input
                  id="serviceTime"
                  name="serviceTime"
                  type="time"
                  value={formData.serviceTime}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  name="location"
                  type="text"
                  placeholder="Enter service location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="numberOfGuests">Number of Guests *</Label>
              <Input
                id="numberOfGuests"
                name="numberOfGuests"
                type="number"
                placeholder="2"
                min="1"
                value={formData.numberOfGuests}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Menu Preferences Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold pb-2 border-b">Menu Preferences</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cuisinePreference">Cuisine Preference *</Label>
                <Select
                  value={formData.cuisinePreference}
                  onValueChange={(value) => handleSelectChange("cuisinePreference", value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select cuisine preference" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nigerian">Nigerian</SelectItem>
                    <SelectItem value="continental">Continental</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                    <SelectItem value="mixed">Mixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="spiceLevel">Spice Level Preference *</Label>
                <Select
                  value={formData.spiceLevel}
                  onValueChange={(value) => handleSelectChange("spiceLevel", value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select spice level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mild">Mild</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hot">Hot</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="specificDishes">Specific Dishes or Ideas You Have in Mind</Label>
              <Textarea
                id="specificDishes"
                name="specificDishes"
                placeholder="List any specific dishes, recipes, or culinary ideas you'd like to explore"
                rows={3}
                value={formData.specificDishes}
                onChange={handleChange}
                className="resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dietaryRestrictions">Dietary Restrictions or Allergies</Label>
              <Textarea
                id="dietaryRestrictions"
                name="dietaryRestrictions"
                placeholder="Any dietary restrictions, allergies, or food preferences we should know about"
                rows={3}
                value={formData.dietaryRestrictions}
                onChange={handleChange}
                className="resize-none"
              />
            </div>
          </div>

          {/* Meal Structure Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold pb-2 border-b">Meal Structure</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="numberOfCourses">Number of Courses *</Label>
                <Select
                  value={formData.numberOfCourses}
                  onValueChange={(value) => handleSelectChange("numberOfCourses", value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select courses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="two">Two</SelectItem>
                    <SelectItem value="three">Three</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="servingStyle">Serving Style *</Label>
                <Select
                  value={formData.servingStyle}
                  onValueChange={(value) => handleSelectChange("servingStyle", value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select serving style" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plated">Plated</SelectItem>
                    <SelectItem value="family-style">Family Style</SelectItem>
                    <SelectItem value="buffet">Buffet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ingredientsProvided">Will Ingredients be Provided by Chef? *</Label>
                <Select
                  value={formData.ingredientsProvided}
                  onValueChange={(value) => handleSelectChange("ingredientsProvided", value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="discuss">Discuss</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Budget and Expectation Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold pb-2 border-b">Budget and Expectation</h3>
            <div className="space-y-2">
              <Label htmlFor="budgetRange">Estimated Budget Range *</Label>
              <Input
                id="budgetRange"
                name="budgetRange"
                type="text"
                placeholder="e.g., ₦50,000 - ₦100,000"
                value={formData.budgetRange}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Additional Notes Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold pb-2 border-b">Additional Notes</h3>
            <div className="space-y-2">
              <Label htmlFor="additionalNotes">Special Requests, Themes, or Instructions</Label>
              <Textarea
                id="additionalNotes"
                name="additionalNotes"
                placeholder="Any special requests, themes, dietary preferences, or specific instructions for the chef..."
                rows={4}
                value={formData.additionalNotes}
                onChange={handleChange}
                className="resize-none"
              />
            </div>
          </div>

          {/* Note */}
          <div className="flex items-start gap-3 p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
            <Clock className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Note:</span> We'll contact you via your preferred method to discuss your requirements and confirm availability.
            </p>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-white"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Submit Request
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

